/**
 * Web Storage - IndexedDB owned by the web data layer.
 *
 * Replaces the legacy Kysely/SQL.js implementation and core-owned storage.
 * Provides a compatible interface for the rest of the app.
 */

import { api, crypto, initSwitchCore, isInitialized } from "@/core_bridge/module_api";
import { Message, Thread, Community, PublicKeyEntry } from "@/core_bridge/module_api";
import { createDirectThreadKey, createGroupThreadKey } from "@/core_bridge/thread_keys";
import { Group, Member, Contact } from "@/data/schemas";
import { extractContactPublicKeys, serializeContactPublicKeys } from "@/data/adapters/contact_keys";

// Storage key for legacy encryption key in localStorage (no longer used)
const STORAGE_KEY_NAME = "switch-storage-key";
const BASE_DB_NAME = "switch-db";
const DB_VERSION = 1;

const STORE_SETTINGS = "settings";
const STORE_CONTACTS = "contacts";
const STORE_THREADS = "threads";
const STORE_MESSAGES = "messages";
const STORE_COMMUNITIES = "communities";

type SettingRecord = { key: string; value: string };
type AppendMessageOptions = { incrementUnread?: boolean };

let dbInstance: IDBDatabase | null = null;

function getDbName(): string {
  if (typeof window === "undefined" || !window.location?.hostname) {
    return `${BASE_DB_NAME}:default`;
  }
  const host = window.location.hostname.toLowerCase().replace(/[^a-z0-9.-]/g, "_");
  return `${BASE_DB_NAME}:${host}`;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getDb(): Promise<IDBDatabase> {
  if (!dbInstance) {
    await openDatabase();
  }
  if (!dbInstance) throw new Error("Storage not initialized");
  return dbInstance;
}

async function dbGet<T>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await getDb();
  const tx = db.transaction(storeName, "readonly");
  const store = tx.objectStore(storeName);
  const value = await requestToPromise(store.get(key));
  return value === undefined ? undefined : (value as T);
}

async function dbGetAll<T>(storeName: string): Promise<T[]> {
  const db = await getDb();
  const tx = db.transaction(storeName, "readonly");
  const store = tx.objectStore(storeName);
  const values = await requestToPromise(store.getAll());
  return (values || []) as T[];
}

async function dbGetAllByIndex<T>(
  storeName: string,
  indexName: string,
  key: IDBValidKey
): Promise<T[]> {
  const db = await getDb();
  const tx = db.transaction(storeName, "readonly");
  const store = tx.objectStore(storeName);
  const index = store.index(indexName);
  const values = await requestToPromise(index.getAll(key));
  return (values || []) as T[];
}

async function dbPut(storeName: string, value: any): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);
  await requestToPromise(store.put(value));
}

async function dbDelete(storeName: string, key: IDBValidKey): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);
  await requestToPromise(store.delete(key));
}

async function dbClear(storeName: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);
  await requestToPromise(store.clear());
}

function normalizeEnvironmentFilter(messageEnv: string | null | undefined, filter?: string | null): boolean {
  const normalized = messageEnv?.toString().trim().toLowerCase();
  // Undefined means "no filter" (show all).
  if (filter === undefined) {
    return true;
  }
  // Null means "standard only" (legacy/no-environment).
  if (filter === null || filter === "") {
    return !normalized || normalized === "standard";
  }
  if (!normalized || normalized === "standard") return true;
  return normalized === filter.toLowerCase();
}

function normalizeMessageForStorage(message: Message): Message {
  let threadKey = message.threadKey;
  const legacyThreadId = (message as any).threadId as string | undefined;
  if (!threadKey) {
    if (message.groupId) {
      threadKey = normalizeGroupThreadKey(message.groupId);
    } else if (legacyThreadId) {
      threadKey = legacyThreadId;
    } else if (message.senderId && message.receiverId) {
      threadKey = resolveDirectThreadKey(message.senderId, message.receiverId);
    }
  }

  let groupId = message.groupId;
  if (!groupId && threadKey?.startsWith("group:")) {
    groupId = threadKey.replace(/^group:/, "");
  }

  const createdAt = typeof message.createdAt === "number" ? message.createdAt : Date.now();
  const updatedAt =
    typeof message.updatedAt === "number"
      ? message.updatedAt
      : typeof message.createdAt === "number"
        ? message.createdAt
        : Date.now();

  return {
    ...message,
    threadKey,
    groupId,
    createdAt,
    updatedAt,
  };
}

async function openIndexedDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(getDbName(), DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_SETTINGS)) {
        db.createObjectStore(STORE_SETTINGS, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(STORE_CONTACTS)) {
        db.createObjectStore(STORE_CONTACTS, { keyPath: "userId" });
      }
      if (!db.objectStoreNames.contains(STORE_THREADS)) {
        const store = db.createObjectStore(STORE_THREADS, { keyPath: "id" });
        store.createIndex("withUserId", "withUserId", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_MESSAGES)) {
        const store = db.createObjectStore(STORE_MESSAGES, { keyPath: "id" });
        store.createIndex("threadKey", "threadKey", { unique: false });
        store.createIndex("groupId", "groupId", { unique: false });
        store.createIndex("communityId", "communityId", { unique: false });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_COMMUNITIES)) {
        db.createObjectStore(STORE_COMMUNITIES, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ============================================================================
// Initialization
// ============================================================================

let isOpen = false;
let openPromise: Promise<void> | null = null;

/**
 * Delete old databases from previous implementations
 */
async function deleteOldDatabases(): Promise<void> {
  const oldDbNames = ["switchapp", "switch-app", "switch_app", "SwitchApp"];

  for (const dbName of oldDbNames) {
    try {
      const request = indexedDB.deleteDatabase(dbName);
      await new Promise<void>((resolve, reject) => {
        request.onsuccess = () => {
          console.log(`Deleted old database: ${dbName}`);
          resolve();
        };
        request.onerror = () => resolve(); // Ignore errors for non-existent DBs
        request.onblocked = () => {
          console.warn(`Database ${dbName} deletion blocked - close other tabs`);
          resolve();
        };
      });
    } catch {
      // Ignore errors
    }
  }
}

/**
 * Initialize the storage database.
 * Generates or retrieves encryption key and opens the database.
 */
export async function openDatabase(): Promise<void> {
  if (isOpen) return;
  if (openPromise) {
    return openPromise;
  }

  openPromise = (async () => {
    // Ensure WASM is initialized first
    if (!isInitialized()) {
      await initSwitchCore();
    }

    // Check if we need to migrate/reset storage
    const migrationKey = "switch-storage-migrated-v3";
    const hasMigrated = localStorage.getItem(migrationKey);

    if (!hasMigrated) {
      console.log("🔄 Resetting local storage...");
      // Delete old databases from Kysely/SQL.js era
      await deleteOldDatabases();
      // Also delete any existing switch-db to start fresh with new schema
      try {
        const deleteRequest = indexedDB.deleteDatabase(getDbName());
        await new Promise<void>((resolve) => {
          deleteRequest.onsuccess = () => resolve();
          deleteRequest.onerror = () => resolve();
          deleteRequest.onblocked = () => resolve();
        });
      } catch {
        // Ignore
      }
      // Clear old storage key (legacy encryption)
      localStorage.removeItem(STORAGE_KEY_NAME);
      localStorage.setItem(migrationKey, "true");
      console.log("✅ Storage reset complete");
    }

    dbInstance = await openIndexedDb();
    isOpen = true;

    console.log(
      "%c📦 IndexedDB storage initialized",
      "color: #2196f3; font-weight: bold;",
      "color: #4caf50;"
    );
  })();

  try {
    await openPromise;
  } finally {
    openPromise = null;
  }
}

export async function closeDatabase(): Promise<void> {
  if (!isOpen) return;
  dbInstance?.close();
  dbInstance = null;
  isOpen = false;
}

async function ensureOpen(): Promise<void> {
  if (!isOpen) {
    await openDatabase();
  }
}

export async function clearAllData(): Promise<void> {
  await ensureOpen();
  await Promise.all([
    dbClear(STORE_SETTINGS),
    dbClear(STORE_CONTACTS),
    dbClear(STORE_THREADS),
    dbClear(STORE_MESSAGES),
    dbClear(STORE_COMMUNITIES),
  ]);
  messageCache.clear();
}

const storage = {
  async open(_name: string, _key: Uint8Array): Promise<void> {
    await openDatabase();
  },
  async close(): Promise<void> {
    await closeDatabase();
  },
  async clearAll(): Promise<void> {
    await clearAllData();
  },
  async saveMessage(message: Message): Promise<void> {
    const normalized = normalizeMessageForStorage(message);
    await dbPut(STORE_MESSAGES, normalized);
  },
  async appendMessage(message: Message, updateThread: boolean, options: AppendMessageOptions = {}): Promise<void> {
    const normalized = normalizeMessageForStorage(message);
    await dbPut(STORE_MESSAGES, normalized);

    if (!updateThread) return;
    const threadKey = normalized.threadKey || normalized.groupId;
    if (!threadKey) return;

    const thread = await dbGet<Thread>(STORE_THREADS, threadKey);
    if (!thread) return;
    const preview = normalized.content?.trim() || "";
    if (!preview) return;
    const messageEnvironment = normalized.environment?.trim().toLowerCase();

    if (messageEnvironment === "hi") {
      const existingHiloMeta = (thread as any).hiloMeta as
        | { lastMessage?: string; lastMessageAt?: number; unread?: number }
        | undefined;
      await dbPut(STORE_THREADS, {
        ...thread,
        hiloMeta: {
          ...existingHiloMeta,
          lastMessage: preview,
          lastMessageAt: normalized.updatedAt ?? Date.now(),
          unread: options.incrementUnread
            ? (existingHiloMeta?.unread || 0) + 1
            : (existingHiloMeta?.unread || 0),
        },
        updatedAt: normalized.updatedAt ?? Date.now(),
      });
      return;
    }

    await dbPut(STORE_THREADS, {
      ...thread,
      lastMessage: preview,
      unread: options.incrementUnread ? (thread.unread || 0) + 1 : (thread.unread || 0),
      updatedAt: normalized.updatedAt ?? Date.now(),
    });
  },
  async getMessage(id: string): Promise<Message | null> {
    return (await dbGet<Message>(STORE_MESSAGES, id)) ?? null;
  },
  async getMessagesForThread(
    threadKey: string,
    limit?: number,
    before?: number,
    environment?: string
  ): Promise<Message[]> {
    let messages = await dbGetAllByIndex<Message>(STORE_MESSAGES, "threadKey", threadKey);
    if (!messages.length && threadKey.startsWith("group:")) {
      const rawId = threadKey.replace(/^group:/, "");
      messages = await dbGetAllByIndex<Message>(STORE_MESSAGES, "groupId", rawId);
    }

    const filtered = messages.filter((msg) => {
      if (before && msg.createdAt >= before) return false;
      if (!normalizeEnvironmentFilter(msg.environment, environment)) return false;
      return true;
    });

    filtered.sort((a, b) => b.createdAt - a.createdAt);
    if (typeof limit === "number") {
      return filtered.slice(0, limit);
    }
    return filtered;
  },
  async deleteMessage(id: string): Promise<void> {
    await dbDelete(STORE_MESSAGES, id);
  },
  async deleteMessagesForThread(threadKey: string): Promise<void> {
    const messages = await dbGetAllByIndex<Message>(STORE_MESSAGES, "threadKey", threadKey);
    for (const msg of messages) {
      await dbDelete(STORE_MESSAGES, msg.id);
    }
  },
  async searchMessages(query: string, threadKey?: string | null, limit: number = 50): Promise<Message[]> {
    const lower = query.toLowerCase();
    let messages: Message[] = [];
    if (threadKey) {
      messages = await dbGetAllByIndex<Message>(STORE_MESSAGES, "threadKey", threadKey);
    } else {
      messages = await dbGetAll<Message>(STORE_MESSAGES);
    }
    const filtered = messages
      .filter((m) => (m.content || "").toLowerCase().includes(lower) && !m.deletedAt)
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, limit);
    return filtered;
  },
  async saveThread(thread: Thread): Promise<void> {
    await dbPut(STORE_THREADS, thread);
  },
  async getThread(id: string): Promise<Thread | null> {
    return (await dbGet<Thread>(STORE_THREADS, id)) ?? null;
  },
  async getAllThreads(): Promise<Thread[]> {
    const threads = await dbGetAll<Thread>(STORE_THREADS);
    return threads.sort((a, b) => b.updatedAt - a.updatedAt);
  },
  async deleteThread(id: string): Promise<void> {
    await dbDelete(STORE_THREADS, id);
  },
  async saveContact(contact: Contact): Promise<void> {
    await dbPut(STORE_CONTACTS, contact);
  },
  async getContact(id: string): Promise<Contact | null> {
    return (await dbGet<Contact>(STORE_CONTACTS, id)) ?? null;
  },
  async getContactByHandle(handle: string): Promise<Contact | null> {
    const contacts = await dbGetAll<Contact>(STORE_CONTACTS);
    const match = contacts.find((c) => c.fullHandle === handle);
    return match || null;
  },
  async getAllContacts(): Promise<Contact[]> {
    return dbGetAll<Contact>(STORE_CONTACTS);
  },
  async deleteContact(id: string): Promise<void> {
    await dbDelete(STORE_CONTACTS, id);
  },
  async saveCommunity(community: Community): Promise<void> {
    await dbPut(STORE_COMMUNITIES, community);
  },
  async getCommunity(id: string): Promise<Community | null> {
    return (await dbGet<Community>(STORE_COMMUNITIES, id)) ?? null;
  },
  async getAllCommunities(): Promise<Community[]> {
    return dbGetAll<Community>(STORE_COMMUNITIES);
  },
  async deleteCommunity(id: string): Promise<void> {
    await dbDelete(STORE_COMMUNITIES, id);
  },
  async setSetting(key: string, value: string): Promise<void> {
    const record: SettingRecord = { key, value };
    await dbPut(STORE_SETTINGS, record);
  },
  async getSetting(key: string): Promise<string | null> {
    const record = await dbGet<SettingRecord>(STORE_SETTINGS, key);
    return record?.value ?? null;
  },
  async deleteSetting(key: string): Promise<void> {
    await dbDelete(STORE_SETTINGS, key);
  },
  async resolvePublicKeys(
    userIds: string[],
    refreshMissing: boolean
  ): Promise<Array<{ userId: string; publicKeysJson: string | null }>> {
    const resolved: Record<string, string | null> = {};
    const missing: string[] = [];

    for (const userId of userIds) {
      const contact = await storage.getContact(userId);
      const normalized = contact
        ? serializeContactPublicKeys(extractContactPublicKeys(contact))
        : null;
      resolved[userId] = normalized;
      if (!normalized) missing.push(userId);
    }

    if (refreshMissing && missing.length) {
      const uniqueMissing = Array.from(new Set(missing));
      for (const userId of uniqueMissing) {
        try {
          const response = await api.searchUserId(userId);
          const user = response?.user;
          if (!user) continue;

          const rawKeys = user.publicKeys || user.public_keys || [];
          const apiDeviceId =
            user.deviceId ||
            user.device_id ||
            (Array.isArray(rawKeys) ? rawKeys[0]?.deviceId || rawKeys[0]?.device_id : undefined) ||
            "default";
          const normalizedKeys = (Array.isArray(rawKeys) ? rawKeys : [])
            .map((entry: any) => {
              const publicKey = entry?.publicKey ?? entry?.public_key;
              if (!publicKey) return null;
              const deviceId = entry?.deviceId ?? entry?.device_id ?? apiDeviceId ?? "default";
              return {
                publicKey: String(publicKey).replace(/\\s+/g, ""),
                deviceId,
                mlKemPublicKey: entry?.mlKemPublicKey ?? entry?.ml_kem_public_key ?? undefined,
          signingPublicKey: entry?.signingPublicKey ?? entry?.signing_public_key ?? null,
              };
            })
            .filter(Boolean);
          const apiPublicKey = user.publicKey || user.public_key;
          if (!normalizedKeys.length && apiPublicKey) {
            normalizedKeys.push({
              publicKey: String(apiPublicKey).replace(/\\s+/g, ""),
              deviceId: apiDeviceId,
              mlKemPublicKey: undefined,
              signingPublicKey: null,
            });
          }

          const serializedKeys = serializeContactPublicKeys(normalizedKeys, apiDeviceId);
          const normalized = serializedKeys ?? apiPublicKey ?? null;
          if (!normalized) continue;

          const contact: Contact = {
            ...(await storage.getContact(userId)),
            ...user,
            userId: user.userId || user.user_id || userId,
            deviceId: apiDeviceId,
            publicKeys: normalizedKeys,
            publicKey: normalized,
            isBlocked: false,
            isMuted: false,
          } as Contact;

          await storage.saveContact(contact);
          await saveContactSupplement(contact);
          resolved[userId] = normalized;
        } catch {
          continue;
        }
      }
    }

    return userIds.map((userId) => ({
      userId,
      publicKeysJson: resolved[userId] ?? null,
    }));
  },
};

// ============================================================================
// App State (Settings-based)
// ============================================================================

export interface AppStateTable {
  id: number;
  appToken: string;
  publicKey: string;
  secretKey: string;
  mlKemPublicKey?: string;
  mlKemSecretKey?: string;
  /**
   * Ed25519 keypair authorizing this device's writes to encrypted
   * thread/community blobs. The secret never leaves the device.
   */
  signingPublicKey?: string;
  signingSecretKey?: string;
  displayPhotoKey: string;
  coverPhotoKey: string;
  profileKey: string;
  profileJson: string;
  updatedAt: number;
}

const SETTING_KEYS = {
  appToken: "app:token",
  publicKey: "app:publicKey",
  secretKey: "app:secretKey",
  mlKemPublicKey: "app:mlKemPublicKey",
  mlKemSecretKey: "app:mlKemSecretKey",
  signingPublicKey: "app:signingPublicKey",
  signingSecretKey: "app:signingSecretKey",
  displayPhotoKey: "app:displayPhotoKey",
  profilePhotos: "app:profilePhotos",
  coverPhotoKey: "app:coverPhotoKey",
  profileKey: "app:profileKey",
  profileJson: "app:profileJson",
  updatedAt: "app:updatedAt",
} as const;

/**
 * True when app state exists, i.e. any persisted app_state field is set.
 *
 * Retrieval MUST NOT be gated on the app token: a keypair is generated during
 * registration before the token is stored, so a token-only check would report
 * "no state" and cause the keypair to be regenerated — diverging from the
 * public key already uploaded to the server. This mirrors iOS/Android, which
 * key app-state retrieval on row presence rather than on the token.
 */
export function appStateHasAnyField(
  fields: Record<string, string | null | undefined>
): boolean {
  return Object.values(fields).some((v) => !!v);
}

export async function getAppState(): Promise<AppStateTable | undefined> {
  await ensureOpen();
  const [
    appToken,
    publicKey,
    secretKey,
    mlKemPublicKey,
    mlKemSecretKey,
    displayPhotoKey,
    coverPhotoKey,
    profileKey,
    profileJson,
    updatedAt,
    signingPublicKey,
    signingSecretKey,
  ] =
    await Promise.all([
      storage.getSetting(SETTING_KEYS.appToken),
      storage.getSetting(SETTING_KEYS.publicKey),
      storage.getSetting(SETTING_KEYS.secretKey),
      storage.getSetting(SETTING_KEYS.mlKemPublicKey),
      storage.getSetting(SETTING_KEYS.mlKemSecretKey),
      storage.getSetting(SETTING_KEYS.displayPhotoKey),
      storage.getSetting(SETTING_KEYS.coverPhotoKey),
      storage.getSetting(SETTING_KEYS.profileKey),
      storage.getSetting(SETTING_KEYS.profileJson),
      storage.getSetting(SETTING_KEYS.updatedAt),
      storage.getSetting(SETTING_KEYS.signingPublicKey),
      storage.getSetting(SETTING_KEYS.signingSecretKey),
    ]);

  // Return state whenever ANY app_state field exists, mirroring iOS/Android
  // (which key retrieval on row presence, not on the token). Gating on the
  // token dropped pre-auth state — losing a keypair generated during
  // registration before the token was stored, forcing regeneration and a
  // pubkey that diverges from the one uploaded to the server.
  if (
    !appStateHasAnyField({
      appToken,
      publicKey,
      secretKey,
      mlKemPublicKey,
      mlKemSecretKey,
      displayPhotoKey,
      coverPhotoKey,
      profileKey,
      profileJson,
      updatedAt,
      signingPublicKey,
      signingSecretKey,
    })
  ) {
    return undefined;
  }

  return {
    id: 0,
    appToken: appToken || "",
    publicKey: publicKey || "",
    secretKey: secretKey || "",
    mlKemPublicKey: mlKemPublicKey || "",
    mlKemSecretKey: mlKemSecretKey || "",
    signingPublicKey: signingPublicKey || "",
    signingSecretKey: signingSecretKey || "",
    displayPhotoKey: displayPhotoKey || "",
    coverPhotoKey: coverPhotoKey || "",
    profileKey: profileKey || "",
    profileJson: profileJson || "",
    updatedAt: updatedAt ? parseInt(updatedAt, 10) : 0,
  };
}

export async function insertAppState(appState: AppStateTable): Promise<void> {
  await ensureOpen();
  await Promise.all([
    storage.setSetting(SETTING_KEYS.appToken, appState.appToken || ""),
    storage.setSetting(SETTING_KEYS.publicKey, appState.publicKey || ""),
    storage.setSetting(SETTING_KEYS.secretKey, appState.secretKey || ""),
    storage.setSetting(SETTING_KEYS.mlKemPublicKey, appState.mlKemPublicKey || ""),
    storage.setSetting(SETTING_KEYS.mlKemSecretKey, appState.mlKemSecretKey || ""),
    storage.setSetting(SETTING_KEYS.signingPublicKey, appState.signingPublicKey || ""),
    storage.setSetting(SETTING_KEYS.signingSecretKey, appState.signingSecretKey || ""),
    storage.setSetting(SETTING_KEYS.displayPhotoKey, appState.displayPhotoKey || ""),
    storage.setSetting(SETTING_KEYS.coverPhotoKey, appState.coverPhotoKey || ""),
    storage.setSetting(SETTING_KEYS.profileKey, appState.profileKey || ""),
    storage.setSetting(SETTING_KEYS.profileJson, appState.profileJson || ""),
    storage.setSetting(SETTING_KEYS.updatedAt, String(appState.updatedAt || Date.now())),
  ]);
}

export async function updateAppState(appState: AppStateTable): Promise<void> {
  await ensureOpen();
  await insertAppState({
    ...appState,
    updatedAt: Date.now(),
  });
}

export async function deleteAppState(): Promise<void> {
  await ensureOpen();
  await Promise.all(
    Object.values(SETTING_KEYS).map((key) => storage.deleteSetting(key))
  );
}

export async function getAppToken(): Promise<string | undefined> {
  await ensureOpen();
  return storage.getSetting(SETTING_KEYS.appToken) || undefined;
}

export async function updateAppToken(token: string): Promise<void> {
  await ensureOpen();
  await storage.setSetting(SETTING_KEYS.appToken, token);
  await storage.setSetting(SETTING_KEYS.updatedAt, String(Date.now()));
}

export async function getProfileJson(): Promise<string | undefined> {
  await ensureOpen();
  return storage.getSetting(SETTING_KEYS.profileJson) || undefined;
}

export async function updateProfile(profileJson: string): Promise<void> {
  // Validate JSON
  JSON.parse(profileJson);
  await ensureOpen();
  await storage.setSetting(SETTING_KEYS.profileJson, profileJson);
  await storage.setSetting(SETTING_KEYS.updatedAt, String(Date.now()));
}

export async function getPublicKey(): Promise<string | undefined> {
  await ensureOpen();
  return storage.getSetting(SETTING_KEYS.publicKey) || undefined;
}

export async function getSecretKey(): Promise<string | undefined> {
  await ensureOpen();
  return storage.getSetting(SETTING_KEYS.secretKey) || undefined;
}

export async function updateKeyPair(
  publicKey: string,
  secretKey: string,
  mlKemPublicKey?: string,
  mlKemSecretKey?: string
): Promise<void> {
  await ensureOpen();
  const updates = [
    storage.setSetting(SETTING_KEYS.publicKey, publicKey),
    storage.setSetting(SETTING_KEYS.secretKey, secretKey),
    storage.setSetting(SETTING_KEYS.updatedAt, String(Date.now())),
  ];
  if (mlKemPublicKey !== undefined) {
    updates.push(storage.setSetting(SETTING_KEYS.mlKemPublicKey, mlKemPublicKey));
  }
  if (mlKemSecretKey !== undefined) {
    updates.push(storage.setSetting(SETTING_KEYS.mlKemSecretKey, mlKemSecretKey));
  }
  await Promise.all(updates);
}

/**
 * Persist this device's Ed25519 signing keypair.
 *
 * Separate from updateKeyPair: the signing keypair has its own lifecycle (a
 * device provisioned before signing existed generates one later without
 * touching its identity keypair, per device-key-immutability).
 */
export async function updateSigningKeyPair(
  signingPublicKey: string,
  signingSecretKey: string
): Promise<void> {
  await ensureOpen();
  await Promise.all([
    storage.setSetting(SETTING_KEYS.signingPublicKey, signingPublicKey),
    storage.setSetting(SETTING_KEYS.signingSecretKey, signingSecretKey),
    storage.setSetting(SETTING_KEYS.updatedAt, String(Date.now())),
  ]);
}

export async function saveDisplayPhotoKey(displayPhotoKey: string): Promise<void> {
  await ensureOpen();
  await storage.setSetting(SETTING_KEYS.displayPhotoKey, displayPhotoKey);
  await storage.setSetting(SETTING_KEYS.updatedAt, String(Date.now()));
}

export async function saveCoverPhotoKey(coverPhotoKey: string): Promise<void> {
  await ensureOpen();
  await storage.setSetting(SETTING_KEYS.coverPhotoKey, coverPhotoKey);
  await storage.setSetting(SETTING_KEYS.updatedAt, String(Date.now()));
}

export async function getDisplayPhotoKey(): Promise<string | undefined> {
  await ensureOpen();
  return storage.getSetting(SETTING_KEYS.displayPhotoKey) || undefined;
}

export async function getCoverPhotoKey(): Promise<string | undefined> {
  await ensureOpen();
  return storage.getSetting(SETTING_KEYS.coverPhotoKey) || undefined;
}

// ============================================================================
// Profile Photos (cached metadata)
// ============================================================================

export interface StoredProfilePhoto {
  userId: string;
  url: string;
  keyBase64?: string;
  cachePath?: string;
  updatedAt?: number;
}

function parseProfilePhotoMap(raw: string | null): Record<string, StoredProfilePhoto> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as Record<string, StoredProfilePhoto>;
    }
  } catch {
    // ignore parse failure
  }
  return {};
}

export async function getProfilePhotos(): Promise<Record<string, StoredProfilePhoto>> {
  await ensureOpen();
  const raw = await storage.getSetting(SETTING_KEYS.profilePhotos);
  return parseProfilePhotoMap(raw);
}

export async function saveProfilePhotoEntry(entry: StoredProfilePhoto): Promise<void> {
  await ensureOpen();
  const raw = await storage.getSetting(SETTING_KEYS.profilePhotos);
  const map = parseProfilePhotoMap(raw);
  map[entry.userId] = {
    ...map[entry.userId],
    ...entry,
    updatedAt: entry.updatedAt || Date.now(),
  };
  await storage.setSetting(SETTING_KEYS.profilePhotos, JSON.stringify(map));
  await storage.setSetting(SETTING_KEYS.updatedAt, String(Date.now()));
}

export async function removeProfilePhotoEntry(userId: string): Promise<void> {
  await ensureOpen();
  const raw = await storage.getSetting(SETTING_KEYS.profilePhotos);
  if (!raw) return;
  const map = parseProfilePhotoMap(raw);
  delete map[userId];
  await storage.setSetting(SETTING_KEYS.profilePhotos, JSON.stringify(map));
  await storage.setSetting(SETTING_KEYS.updatedAt, String(Date.now()));
}

// Additional app state settings keys
const EXTRA_SETTING_KEYS = {
  communityToken: "app:communityToken",
  deviceId: "app:deviceId",
  phoneNumber: "app:phoneNumber",
  jitsiToken: "app:jitsiToken",
  privacyInitialized: "app:privacyInitialized",
} as const;

const CONTACT_SUPPLEMENT_PREFIX = "contact:supplement:";
type ContactSupplement = {
  deviceId?: string | null;
  publicKeys?: PublicKeyEntry[] | null;
  appId?: string | null;
  displayPhotoMedia?: {
    url: string;
    keyBase64?: string | null;
    mimeType?: string | null;
  } | null;
};

async function saveContactSupplement(contact: Contact): Promise<void> {
  // Persist fields not stored in Contact (deviceId, publicKeys, appId) in the encrypted settings store
  if (!contact?.userId) return;
  const primaryDeviceId =
    (contact as any).deviceId ||
    (contact as any).publicKeys?.[0]?.deviceId ||
    "default";
  const payload: ContactSupplement = {
    deviceId: primaryDeviceId,
    publicKeys: (contact as any).publicKeys ?? null,
    appId: (contact as any).appId ?? null,
    displayPhotoMedia: (contact as any).displayPhotoMedia ?? null,
  };
  await storage.setSetting(
    `${CONTACT_SUPPLEMENT_PREFIX}${contact.userId}`,
    JSON.stringify(payload)
  );
}

async function getContactSupplement(userId: string): Promise<ContactSupplement | undefined> {
  const stored = await storage.getSetting(`${CONTACT_SUPPLEMENT_PREFIX}${userId}`);
  if (!stored) return undefined;
  try {
    return JSON.parse(stored) as ContactSupplement;
  } catch {
    return undefined;
  }
}

export async function saveMyTokenAndId(
  token: string,
  communityToken: string,
  deviceId: string,
  phoneNumber: string
): Promise<void> {
  await ensureOpen();
  await Promise.all([
    storage.setSetting(SETTING_KEYS.appToken, token),
    storage.setSetting(EXTRA_SETTING_KEYS.communityToken, communityToken),
    storage.setSetting(EXTRA_SETTING_KEYS.deviceId, deviceId),
    storage.setSetting(EXTRA_SETTING_KEYS.phoneNumber, phoneNumber),
    storage.setSetting(SETTING_KEYS.updatedAt, String(Date.now())),
  ]);
}

export async function insertPrivacyState(): Promise<void> {
  // Initialize privacy settings if not already done
  await ensureOpen();
  const initialized = await storage.getSetting(EXTRA_SETTING_KEYS.privacyInitialized);
  if (!initialized) {
    await storage.setSetting(EXTRA_SETTING_KEYS.privacyInitialized, "true");
  }
}

export async function saveJitsiToken(token: string): Promise<void> {
  await ensureOpen();
  await storage.setSetting(EXTRA_SETTING_KEYS.jitsiToken, token);
  await storage.setSetting(SETTING_KEYS.updatedAt, String(Date.now()));
}

export async function getJitsiToken(): Promise<string | undefined> {
  await ensureOpen();
  return storage.getSetting(EXTRA_SETTING_KEYS.jitsiToken) || undefined;
}

// ============================================================================
// Users / Contacts
// ============================================================================

/**
 * Convert Contact to Contact for storage
 */
function userToContact(user: Contact): Contact {
  const rawKeys = (user as any).publicKeys ?? (user as any).public_keys ?? null;
  const deviceId = (user as any).deviceId ?? (user as any).device_id ?? undefined;
  const serializedKeys = serializeContactPublicKeys(rawKeys, deviceId);
  const existingPublicKey =
    (user as any).publicKey ?? (user as any).public_key ?? null;

  const stored: any = {
    userId: user.userId,
    username: user.username,
    discriminator: user.discriminator,
    fullHandle: (user as any).fullHandle ?? (user as any).full_handle ?? null,
    threadKey: null, // Will be set when thread is created
    publicKey: serializedKeys ?? existingPublicKey,
    displayPhoto: user.displayPhoto || null,
    coverPhoto: user.coverPhoto || null,
    status: user.status || null,
    bio: user.bio || null,
    lastSeen: user.lastSeen || null,
    isBlocked: (user as any).isBlocked ?? (user as any).isBlock ?? false,
    isMuted: (user as any).isMuted ?? (user as any).isMute ?? false,
    createdAt: user.createdAt || null,
    updatedAt: user.updatedAt || null,
    displayPhotoMedia: (user as any).displayPhotoMedia ?? null,
  };
  return stored as Contact;
}

/**
 * Convert Contact to Contact for compatibility
 */
function contactToUser(contact: Contact, supplement?: ContactSupplement): Contact {
  const merged: any = {
    ...contact,
    publicKeys: supplement?.publicKeys ?? extractContactPublicKeys(contact),
    deviceId:
      supplement?.deviceId ??
      (supplement?.publicKeys?.[0]?.deviceId || undefined),
    appId: supplement?.appId ?? (contact as any).appId,
    displayPhotoMedia: supplement?.displayPhotoMedia ?? (contact as any).displayPhotoMedia,
  };
  return merged as Contact;
}

export async function insertUser(user: Contact): Promise<void> {
  await ensureOpen();
  const contact = userToContact(user);
  await storage.saveContact(contact);
  await saveContactSupplement(user);
}

export async function getUser(userId: string): Promise<Contact | undefined> {
  await ensureOpen();
  const contact = await storage.getContact(userId);
  if (!contact) return undefined;
  const supplement = await getContactSupplement(contact.userId);
  const user = contactToUser(contact, supplement);
  if (supplement) {
    user.deviceId = supplement.deviceId ?? user.deviceId ?? "default";
    if (supplement.appId !== undefined) user.appId = supplement.appId as any;
  }
  return user;
}

export interface ResolvedPublicKeys {
  userId: string;
  publicKeysJson: string | null;
}

export async function resolvePublicKeys(
  userIds: string[],
  refreshMissing: boolean
): Promise<ResolvedPublicKeys[]> {
  await ensureOpen();
  const resolved = await storage.resolvePublicKeys(userIds, refreshMissing);
  return (resolved || []) as ResolvedPublicKeys[];
}

export async function getUsers(): Promise<Contact[]> {
  await ensureOpen();
  const contacts = await storage.getAllContacts();
  const results: Contact[] = [];
  for (const contact of contacts || []) {
    const supplement = await getContactSupplement(contact.userId);
    const user = contactToUser(contact, supplement);
    if (supplement) {
      user.deviceId = supplement.deviceId ?? user.deviceId ?? "default";
      if (supplement.appId !== undefined) user.appId = supplement.appId as any;
    }
    results.push(user);
  }
  return results;
}

export async function updateUser(userId: string, updates: Partial<Contact>): Promise<void> {
  await ensureOpen();
  const existing = await storage.getContact(userId);
  if (!existing) return;

  const updated: Contact = {
    ...existing,
    ...userToContact({ ...contactToUser(existing), ...updates } as Contact),
    updatedAt: Date.now(),
  };
  await storage.saveContact(updated);
  await saveContactSupplement({ ...contactToUser(updated), ...updates, userId } as Contact);
}

export async function getUserByUsername(username: string): Promise<Contact | undefined> {
  await ensureOpen();
  const contacts = await storage.getAllContacts();
  const match = (contacts || []).find(
    (c) => c.username?.toLowerCase() === username.toLowerCase()
  );
  if (!match) return undefined;
  const supplement = await getContactSupplement(match.userId);
  return contactToUser(match, supplement);
}

export async function hasDirectThreadWithUser(userId: string): Promise<boolean> {
  await ensureOpen();
  const threads = await storage.getAllThreads();
  return (threads || []).some((t) => (t as any).withUserId === userId);
}

// ============================================================================
// Groups / Threads
// ============================================================================

/**
 * Convert Group to Thread for storage
 */
function groupToThread(group: Group): Thread {
  const legacyGroupKey = (group as any).groupKey as string | undefined;
  const derivedGroupKeys =
    group.groupKeys || (legacyGroupKey ? { 1: legacyGroupKey } : null);
  const derivedKeyVersion =
    group.currentKeyVersion || (derivedGroupKeys ? 1 : null);
  return {
    id: group.id,
    name: group.name || "",
    description: group.description || null,
    communityId: group.communityId || null,
    ownerId: group.ownerId || null,
    groupKeys: derivedGroupKeys,
    currentKeyVersion: derivedKeyVersion,
    members: group.members || [],
    membershipStatus: group.membershipStatus || null,
    image: group.image || null,
    unread: group.unread || 0,
    lastMessage: group.lastMessage || null,
    hiloMeta: group.hiloMeta || null,
    isAnnouncementGroup: group.isAnnouncementGroup || null,
    isDefaultGroup: group.isDefaultGroup || null,
    isStandaloneGroup: group.isStandaloneGroup || null,
    isMute: group.isMute || null,
    isBlock: group.isBlock || null,
    version: group.version || null,
    createdAt: group.createdAt || Date.now(),
    updatedAt: group.updatedAt || Date.now(),
  } as unknown as Thread;
}

/**
 * Convert Thread to Group for compatibility
 */
function threadToGroup(thread: Thread): Group {
  return thread as Group;
}

export async function getGrp(groupId: string): Promise<Group | undefined> {
  await ensureOpen();
  const thread = await storage.getThread(groupId);
  return thread ? threadToGroup(thread) : undefined;
}

export async function insertGrp(group: Group): Promise<void> {
  await ensureOpen();
  const thread = groupToThread(group);
  await storage.saveThread(thread);
}

export async function getGrps(): Promise<Group[]> {
  await ensureOpen();
  const threads = await storage.getAllThreads();
  return (threads || []).map(threadToGroup);
}

export async function updateGrpMembershipStatus(groupId: string, membershipStatus: any): Promise<void> {
  await ensureOpen();
  const existing = await storage.getThread(groupId);
  if (!existing) return;

  await storage.saveThread({
    ...existing,
    membershipStatus,
    updatedAt: Date.now(),
  });
}

export async function updateGrpMembers(groupId: string, members: Member[] | null | undefined): Promise<void> {
  await ensureOpen();
  const existing = await storage.getThread(groupId);
  if (!existing) return;

  await storage.saveThread({
    ...existing,
    members: members || [],
    updatedAt: Date.now(),
  });
}

/**
 * @deprecated Use updateGrpKeys for new code supporting key rotation
 */
export async function updateGrpKey(groupId: string, groupKey: string): Promise<void> {
  await ensureOpen();
  const existing = await storage.getThread(groupId);
  if (!existing) return;

  await storage.saveThread({
    ...existing,
    groupKeys: { 1: groupKey },
    currentKeyVersion: 1,
    updatedAt: Date.now(),
  });
}

/**
 * Update group keys with versioning support for key rotation
 */
export async function updateGrpKeys(
  groupId: string,
  groupKeys: Record<number, string>,
  currentKeyVersion: number
): Promise<void> {
  await ensureOpen();
  const existing = await storage.getThread(groupId);
  if (!existing) return;

  await storage.saveThread({
    ...existing,
    groupKeys,
    currentKeyVersion,
    updatedAt: Date.now(),
  });
}

/**
 * Add a new key version (for key rotation)
 */
export async function addGrpKeyVersion(
  groupId: string,
  newKey: string,
  newVersion: number
): Promise<void> {
  await ensureOpen();
  const existing = await storage.getThread(groupId);
  if (!existing) return;

  const groupKeys = (existing as any).groupKeys || {};
  groupKeys[newVersion] = newKey;

  await storage.saveThread({
    ...existing,
    groupKeys,
    currentKeyVersion: newVersion,
    updatedAt: Date.now(),
  });
}

/**
 * Get the current encryption key for a group
 * Returns [key, version] tuple
 */
export async function getGrpCurrentKey(groupId: string): Promise<[string, number] | null> {
  await ensureOpen();
  const thread = await storage.getThread(groupId);
  if (!thread) return null;

  const groupKeys = (thread as any).groupKeys as Record<number, string> | undefined;
  const currentVersion = (thread as any).currentKeyVersion as number | undefined;

  // New format: groupKeys map
  if (groupKeys && currentVersion && groupKeys[currentVersion]) {
    return [groupKeys[currentVersion], currentVersion];
  }

  // Legacy format: single groupKey (treated as version 1)
  const legacyKey = (thread as any).groupKey as string | undefined;
  if (legacyKey) {
    return [legacyKey, 1];
  }

  return null;
}

/**
 * Get the current group key with version (alias for getGrpCurrentKey)
 * Returns [key, version] tuple or null
 */
export async function getGroupKeyWithVersion(groupId: string): Promise<[string, number] | null> {
  return getGrpCurrentKey(groupId);
}

/**
 * Rotate group key - generates new key and increments version
 * Preserves all old keys for backward decryption
 */
export async function rotateGroupKeyInStorage(groupId: string): Promise<void> {
  await ensureOpen();
  const thread = await storage.getThread(groupId);
  if (!thread) {
    throw new Error(`Thread not found: ${groupId}`);
  }

  // Generate new key using crypto module
  const newKeyBytes = crypto.secretboxKeygen();
  const newKey = btoa(String.fromCharCode(...newKeyBytes));

  // Get current version and keys
  const groupKeys = ((thread as any).groupKeys as Record<number, string>) || {};
  const currentVersion = ((thread as any).currentKeyVersion as number) || 1;
  const newVersion = currentVersion + 1;

  // If no groupKeys but has legacy groupKey, migrate it
  if (Object.keys(groupKeys).length === 0 && (thread as any).groupKey) {
    groupKeys[1] = (thread as any).groupKey;
  }

  // Add new key
  groupKeys[newVersion] = newKey;

  // Save updated thread
  await storage.saveThread({
    ...thread,
    groupKeys,
    currentKeyVersion: newVersion,
    updatedAt: Date.now(),
  });

  console.log(`[storage] Rotated group key for ${groupId} to version ${newVersion}`);
}

/**
 * Get a specific key version for decryption
 */
export async function getGrpKeyByVersion(
  groupId: string,
  version: number
): Promise<string | null> {
  await ensureOpen();
  const thread = await storage.getThread(groupId);
  if (!thread) return null;

  const groupKeys = (thread as any).groupKeys as Record<number, string> | undefined;

  // New format
  if (groupKeys && groupKeys[version]) {
    return groupKeys[version];
  }

  // Legacy format: version 1 is the single groupKey
  if (version === 1) {
    return (thread as any).groupKey || null;
  }

  return null;
}

export async function updateGroupLastMessage(groupId: string, lastMessage: string, updatedAt: number): Promise<void> {
  await ensureOpen();
  const existing = await storage.getThread(groupId);
  if (!existing) return;

  await storage.saveThread({
    ...existing,
    lastMessage,
    unread: (existing.unread || 0) + 1,
    updatedAt,
  });
}

export async function updateHiloMeta(groupId: string, lastMessage: string, lastMessageAt: number): Promise<void> {
  await ensureOpen();
  const existing = await storage.getThread(groupId);
  if (!existing) return;

  await storage.saveThread({
    ...existing,
    hiloMeta: {
      lastMessage,
      lastMessageAt,
      unread: ((existing.hiloMeta as any)?.unread || 0) + 1,
    },
    updatedAt: Date.now(),
  });
}

export async function resetUnread(groupId: string): Promise<void> {
  await ensureOpen();
  const existing = await storage.getThread(groupId);
  if (!existing) return;

  await storage.saveThread({
    ...existing,
    unread: 0,
    updatedAt: Date.now(),
  });
}

export async function markThreadUnread(groupId: string): Promise<void> {
  await ensureOpen();
  const existing = await storage.getThread(groupId);
  if (!existing) return;
  // Boolean signal stored as a count; never lower an existing unread count.
  await storage.saveThread({
    ...existing,
    unread: Math.max(existing.unread || 0, 1),
  });
}

export async function resetUnreadHilo(groupId: string): Promise<void> {
  await ensureOpen();
  const existing = await storage.getThread(groupId);
  if (!existing) return;

  await storage.saveThread({
    ...existing,
    hiloMeta: {
      ...((existing.hiloMeta as any) || {}),
      unread: 0,
    },
    updatedAt: Date.now(),
  });
}

// ============================================================================
// Messages
// ============================================================================

// In-memory message cache for complex queries
// (Rust storage only supports simple thread-based queries)
let messageCache: Map<string, Message> = new Map();

function resolveDirectThreadKey(senderId: string, receiverId: string): string {
  return createDirectThreadKey(senderId, receiverId);
}

function normalizeGroupThreadKey(groupIdOrKey?: string): string | undefined {
  if (!groupIdOrKey) return undefined;
  return groupIdOrKey.startsWith("group:") ? groupIdOrKey : createGroupThreadKey(groupIdOrKey);
}

function resolveThreadKeyForMessage(message: Message): string | undefined {
  if (message.threadKey) return message.threadKey;
  const legacyThreadId = (message as any).threadId as string | undefined;
  if (legacyThreadId) return legacyThreadId;
  if (message.groupId) return normalizeGroupThreadKey(message.groupId);
  if (message.senderId && message.receiverId) {
    return resolveDirectThreadKey(message.senderId, message.receiverId);
  }
  return undefined;
}

async function loadAllMessages(): Promise<Message[]> {
  await ensureOpen();
  // Get all threads and load their messages
  const threads = await storage.getAllThreads();
  const allMessages: Message[] = [];

  for (const thread of threads || []) {
    const msgs = await storage.getMessagesForThread(thread.id, 10000);
    if (msgs) {
      for (const msg of msgs) {
        messageCache.set(msg.id, msg);
        allMessages.push(msg);
      }
    }
  }

  return allMessages;
}

async function rebuildThreadStateAfterHiDestroy(
  remainingMessages: Message[]
): Promise<void> {
  const threads = await storage.getAllThreads();

  for (const thread of threads || []) {
    const visibleMessages = remainingMessages
      .filter((msg) => !msg.deletedAt && resolveThreadKeyForMessage(msg) === thread.id)
      .filter((msg) => {
        const env = msg.environment?.trim().toLowerCase();
        return !env || env === "standard" || env === "lo";
      })
      .sort((a, b) => b.createdAt - a.createdAt);

    const latest = visibleMessages[0];
    const latestPreview = latest?.content?.trim() || null;

    await storage.saveThread({
      ...thread,
      lastMessage: latestPreview,
      unread: 0,
      hiloMeta: null,
      updatedAt: latest?.updatedAt ?? thread.updatedAt,
    } as Thread);
  }
}

export async function getMsg(msgId: string): Promise<Message | undefined> {
  await ensureOpen();
  // Try cache first
  if (messageCache.has(msgId)) {
    return messageCache.get(msgId);
  }

  const msg = await storage.getMessage(msgId);
  if (msg) {
    messageCache.set(msg.id, msg);
  }
  return msg || undefined;
}

export async function insertMsg(msg: Message, options: AppendMessageOptions = {}): Promise<void> {
  await ensureOpen();
  // Ensure threadKey is set for proper indexing in IndexedDB
  // For DMs, compute threadKey from senderId and receiverId if not set
  let messageToSave = msg;
  if (!msg.groupId && !msg.threadKey && msg.senderId && msg.receiverId) {
    const computedThreadKey = resolveDirectThreadKey(msg.senderId, msg.receiverId);
    messageToSave = { ...msg, threadKey: computedThreadKey };
    console.log("[db/storage] insertMsg: computed threadKey for DM:", computedThreadKey);
  }

  console.log("[db/storage] insertMsg:", {
    id: messageToSave.id,
    threadKey: messageToSave.threadKey,
    groupId: messageToSave.groupId,
    senderId: messageToSave.senderId,
    receiverId: messageToSave.receiverId,
  });
  await storage.appendMessage(messageToSave, true, options);
  messageCache.set(messageToSave.id, messageToSave);
}

export async function updateMsg(msg: Message): Promise<void> {
  await ensureOpen();
  // Ensure threadKey is set for proper indexing (same logic as insertMsg)
  let messageToSave = msg;
  if (!msg.groupId && !msg.threadKey && msg.senderId && msg.receiverId) {
    const computedThreadKey = resolveDirectThreadKey(msg.senderId, msg.receiverId);
    messageToSave = { ...msg, threadKey: computedThreadKey };
  }
  await storage.saveMessage(messageToSave);
  messageCache.set(messageToSave.id, messageToSave);
}

export async function deleteMsg(msgId: string): Promise<void> {
  await ensureOpen();
  const msg = await getMsg(msgId);
  if (msg) {
    await storage.saveMessage({
      ...msg,
      content: "",
      deletedAt: Date.now(),
    });
    messageCache.set(msgId, { ...msg, content: "", deletedAt: Date.now() });
  }
}

export async function messageExists(msgId: string): Promise<boolean> {
  await ensureOpen();
  const msg = await getMsg(msgId);
  return !!msg;
}

export async function ackMessage(msgId: string): Promise<void> {
  await ensureOpen();
  console.log("[db/storage] ackMessage called for:", msgId);
  const msg = await getMsg(msgId);
  if (msg) {
    const updated = { ...msg, status: "DELIVERED" as const };
    console.log("[db/storage] ackMessage updating message:", { id: msgId, oldStatus: msg.status, newStatus: "DELIVERED" });
    await storage.saveMessage(updated);
    messageCache.set(msgId, updated);
    console.log("[db/storage] ackMessage saved successfully");
  } else {
    console.warn("[db/storage] ackMessage: message not found:", msgId);
  }
}

export async function getMsgsByGroupId(groupId: string, environment?: string): Promise<Message[]> {
  await ensureOpen();
  const msgs = await storage.getMessagesForThread(groupId, 10000, undefined, environment);
  return (msgs || []).sort((a, b) => a.createdAt - b.createdAt);
}

/**
 * Get messages for a group by groupId
 * @param environment - Optional environment filter ("hi" for hilo mode, or any other value to exclude "hi" messages)
 */
export async function getMessagesByGroupId(
  groupId: string,
  beforeTimestamp?: number,
  limit = 30,
  environment?: string
): Promise<Message[]> {
  await ensureOpen();
  const threadKey = normalizeGroupThreadKey(groupId);
  if (!threadKey) return [];
  console.log("[db/storage] getMessagesByGroupId:", { groupId: threadKey, limit, beforeTimestamp, environment });
  const msgs = await storage.getMessagesForThread(threadKey, limit, beforeTimestamp, environment);
  console.log("[db/storage] getMessagesForThread returned:", { count: msgs?.length || 0 });
  return (msgs || []).reverse(); // Return in chronological order
}

export async function getOlderMessagesByGroupId(
  groupId: string,
  beforeTimestamp?: number,
  limit = 30,
  environment?: string
): Promise<Message[]> {
  return getMessagesByGroupId(groupId, beforeTimestamp, limit, environment);
}

/**
 * Get messages for a thread by threadKey
 * @param environment - Optional environment filter ("hi" for hilo mode, or any other value to exclude "hi" messages)
 */
export async function getMessagesByThreadId(
  threadKey: string,
  beforeTimestamp?: number,
  limit = 30,
  environment?: string
): Promise<Message[]> {
  await ensureOpen();
  console.log("[db/storage] getMessagesByThreadId:", { threadKey, limit, beforeTimestamp, environment });
  const msgs = await storage.getMessagesForThread(threadKey, limit, beforeTimestamp, environment);
  console.log("[db/storage] raw messages from storage:", msgs?.map(m => ({
    id: m.id,
    threadKey: m.threadKey,
    groupId: m.groupId,
    content: m.content?.substring(0, 20)
  })));
  return (msgs || []).reverse();
}

/**
 * @deprecated Use getMessagesByThreadId instead
 */
export async function getMessagesBySenderAndReceiverId(
  receiverId: string,
  senderId: string,
  beforeTimestamp?: number,
  limit = 30
): Promise<Message[]> {
  await ensureOpen();
  // For DMs, the threadKey is deterministic based on user IDs
  const threadKey = resolveDirectThreadKey(senderId, receiverId);
  return getMessagesByThreadId(threadKey, beforeTimestamp, limit);
}

export async function getOlderMessagesBySenderAndReceiverId(
  receiverId: string,
  senderId: string,
  beforeTimestamp?: number,
  limit = 30
): Promise<Message[]> {
  await ensureOpen();
  return getMessagesBySenderAndReceiverId(receiverId, senderId, beforeTimestamp, limit);
}

export async function getMsgsByCommunityId(communityId: string): Promise<Message[]> {
  await ensureOpen();
  // Load all messages and filter by communityId
  const allMsgs = await loadAllMessages();
  return allMsgs
    .filter((m) => m.communityId === communityId)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function deleteMsgsByGroupId(groupId: string, environment?: string): Promise<void> {
  await ensureOpen();
  const msgs = await getMsgsByGroupId(groupId, environment);
  for (const msg of msgs) {
    await storage.deleteMessage(msg.id);
    messageCache.delete(msg.id);
  }
}

export async function deleteMsgsBySenderAndReceiverId(
  senderId: string,
  receiverId: string,
  environment?: string
): Promise<void> {
  await ensureOpen();
  const threadKey = resolveDirectThreadKey(senderId, receiverId);
  const msgs = await storage.getMessagesForThread(threadKey, 10000);
  for (const msg of msgs || []) {
    if (!environment || msg.environment === environment || !msg.environment) {
      await storage.deleteMessage(msg.id);
      messageCache.delete(msg.id);
    }
  }
}

export async function deleteMsgsByCommunityId(communityId: string): Promise<void> {
  await ensureOpen();
  const msgs = await getMsgsByCommunityId(communityId);
  for (const msg of msgs) {
    await storage.deleteMessage(msg.id);
    messageCache.delete(msg.id);
  }
}

export async function deleteMessagesForThread(threadId: string, environment?: string): Promise<void> {
  await ensureOpen();

  if (threadId.startsWith("dm:")) {
    const parts = threadId.replace(/^dm:/, "").split("_");
    if (parts.length === 2) {
      await deleteMsgsBySenderAndReceiverId(parts[0], parts[1], environment);
      return;
    }
    await storage.deleteMessagesForThread(threadId);
    return;
  }

  if (threadId.startsWith("self:")) {
    const selfId = threadId.replace(/^self:/, "");
    if (selfId) {
      await deleteMsgsBySenderAndReceiverId(selfId, selfId, environment);
      return;
    }
    await storage.deleteMessagesForThread(threadId);
    return;
  }

  if (threadId.startsWith("group:")) {
    const groupId = threadId.replace(/^group:/, "");
    await deleteMsgsByGroupId(groupId, environment);
    return;
  }

  // Fallback: try both group-id and thread-key paths
  await deleteMsgsByGroupId(threadId, environment);
  await storage.deleteMessagesForThread(threadId);
}

export async function getLatestMsgByGroupId(groupId: string): Promise<Message | undefined> {
  await ensureOpen();
  const threadKey = normalizeGroupThreadKey(groupId);
  if (!threadKey) return undefined;
  const msgs = await storage.getMessagesForThread(threadKey, 1);
  return msgs?.[0];
}

export async function getLatestMsgByCommunityId(communityId: string): Promise<Message | undefined> {
  await ensureOpen();
  const msgs = await getMsgsByCommunityId(communityId);
  return msgs[msgs.length - 1];
}

export async function destroyAllHiData(): Promise<void> {
  await ensureOpen();
  const allMsgs = await dbGetAll<Message>(STORE_MESSAGES);
  const remainingMessages: Message[] = [];

  for (const msg of allMsgs || []) {
    const env = msg.environment?.trim().toLowerCase();
    if (env === "hi") {
      await storage.deleteMessage(msg.id);
      continue;
    }
    remainingMessages.push(msg);
  }

  messageCache.clear();
  for (const msg of remainingMessages) {
    messageCache.set(msg.id, msg);
  }

  await rebuildThreadStateAfterHiDestroy(remainingMessages);
}

export async function searchMsgs(searchText: string): Promise<Message[]> {
  await ensureOpen();
  const allMsgs = await loadAllMessages();
  const lower = searchText.toLowerCase();
  return allMsgs
    .filter((m) => m.content?.toLowerCase().includes(lower))
    .sort((a, b) => b.createdAt - a.createdAt);
}

export async function searchMessages({
  query,
  threadKey,
}: {
  query: string;
  threadKey: string;
}): Promise<Message[]> {
  await ensureOpen();
  const lower = query.toLowerCase();
  let msgs: Message[];

  if (threadKey.startsWith("group:")) {
    msgs = await getMsgsByGroupId(threadKey);
  } else {
    msgs = await storage.getMessagesForThread(threadKey, 10000) || [];
  }

  return msgs
    .filter((m) => m.content?.toLowerCase().includes(lower) && !m.deletedAt)
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 50);
}

// ============================================================================
// Communities
// ============================================================================

export async function getCommunity(communityId: string): Promise<Community | undefined> {
  await ensureOpen();
  const community = await storage.getCommunity(communityId);
  return community || undefined;
}

export async function getCommunities(): Promise<Community[]> {
  await ensureOpen();
  return (await storage.getAllCommunities()) || [];
}

export async function getActiveCommunities(): Promise<Community[]> {
  await ensureOpen();
  const all = await getCommunities();
  return all.filter((c) => (c as any).isActive !== false);
}

export async function insertCommunity(community: Community): Promise<void> {
  await ensureOpen();
  await storage.saveCommunity(community);
}

export async function insertCommunities(communities: Community[]): Promise<void> {
  await ensureOpen();
  await Promise.all(communities.map((c) => storage.saveCommunity(c)));
}

export async function updateCommunity(communityId: string, updates: Partial<Community>): Promise<void> {
  await ensureOpen();
  const existing = await storage.getCommunity(communityId);
  if (!existing) return;

  await storage.saveCommunity({
    ...existing,
    ...updates,
    updatedAt: Date.now(),
  } as Community);
}

export async function deactivateCommunity(
  communityId: string,
  deactivatedBy: string,
  deactivationReason: string
): Promise<void> {
  await ensureOpen();
  const existing = await storage.getCommunity(communityId);
  if (!existing) return;

  await storage.saveCommunity({
    ...existing,
    isActive: false,
    deactivatedAt: Date.now(),
    deactivatedBy,
    deactivationReason,
    updatedAt: Date.now(),
  } as Community);
}

export async function deleteCommunity(communityId: string): Promise<void> {
  await ensureOpen();
  await storage.deleteCommunity(communityId);
}

export async function deleteThread(threadId: string): Promise<void> {
  await ensureOpen();
  await storage.deleteThread(threadId);
}

// ============================================================================
// Recovery Helpers
// ============================================================================

type MembershipLists = {
  contactIds: string[];
  groupThreadIds: string[];
  communityIds: string[];
};

function buildContactPlaceholder(userId: string, now: number): Contact {
  return {
    userId,
    username: "",
    discriminator: "",
    fullHandle: null,
    threadKey: null,
    publicKey: null,
    displayPhoto: null,
    displayPhotoMedia: null,
    coverPhoto: null,
    status: null,
    bio: null,
    lastSeen: null,
    type: "unregistered",
    createdAt: now,
    updatedAt: now,
    isBlocked: false,
    isMuted: false,
    // Mirrors core's own Contact defaults (core/src/schemas/contact.rs) — a
    // placeholder for an unknown user has never been verified.
    isVerified: false,
    verifiedIdentityKey: null,
  } as Contact;
}

function buildGroupPlaceholder(groupId: string, now: number): Group {
  return {
    id: groupId,
    name: "Unknown Group",
    description: null,
    members: [],
    communityId: null,
    ownerId: null,
    image: null,
    unread: 0,
    createdAt: now,
    updatedAt: now,
    isAnnouncementGroup: false,
    isDefaultGroup: false,
    isStandaloneGroup: false,
    isMute: false,
    isBlock: false,
  } as Group;
}

function buildCommunityPlaceholder(communityId: string, now: number): Community {
  return {
    id: communityId,
    name: "Unknown Community",
    description: null,
    members: [],
    groups: [],
    communityKey: "",
    image: null,
    ownerId: "",
    createdAt: now,
    updatedAt: now,
    isActive: true,
  } as Community;
}

export async function replaceMembershipLists(
  lists: MembershipLists
): Promise<{ contacts: Contact[]; groups: Group[]; communities: Community[] }> {
  await ensureOpen();
  const now = Date.now();

  const existingContacts = await storage.getAllContacts();
  await Promise.all(
    existingContacts.map((contact) =>
      storage.deleteSetting(`${CONTACT_SUPPLEMENT_PREFIX}${contact.userId}`)
    )
  );

  await Promise.all([
    dbClear(STORE_CONTACTS),
    dbClear(STORE_THREADS),
    dbClear(STORE_MESSAGES),
    dbClear(STORE_COMMUNITIES),
  ]);
  messageCache.clear();

  const contactIds = Array.from(new Set(lists.contactIds)).filter(Boolean);
  const groupIds = Array.from(new Set(lists.groupThreadIds)).filter(Boolean);
  const communityIds = Array.from(new Set(lists.communityIds)).filter(Boolean);

  const contacts = contactIds.map((id) => buildContactPlaceholder(id, now));
  const groups = groupIds.map((id) => buildGroupPlaceholder(id, now));
  const communities = communityIds.map((id) => buildCommunityPlaceholder(id, now));

  await Promise.all(contacts.map((contact) => storage.saveContact(contact)));
  await Promise.all(groups.map((group) => storage.saveThread(group)));
  await Promise.all(communities.map((community) => storage.saveCommunity(community)));

  return { contacts, groups, communities };
}

// ============================================================================
// Exports
// ============================================================================

export default {
  // Core
  initialize: openDatabase,
  close: closeDatabase,
  clearAll: clearAllData,

  // Legacy core-storage compatibility (used by older web modules)
  saveMessage: storage.saveMessage,
  appendMessage: storage.appendMessage,
  getMessage: storage.getMessage,
  getMessagesForThread: storage.getMessagesForThread,
  deleteMessage: storage.deleteMessage,
  searchMessages: storage.searchMessages,

  saveThread: storage.saveThread,
  getThread: storage.getThread,
  getAllThreads: storage.getAllThreads,

  saveContact: storage.saveContact,
  getContact: storage.getContact,
  getContactByHandle: storage.getContactByHandle,
  getAllContacts: storage.getAllContacts,
  deleteContact: storage.deleteContact,

  setSetting: storage.setSetting,
  getSetting: storage.getSetting,
  deleteSetting: storage.deleteSetting,

  // App State
  getAppState,
  insertAppState,
  updateAppState,
  deleteAppState,
  getAppToken,
  updateAppToken,
  getProfileJson,
  updateProfile,
  getPublicKey,
  getSecretKey,
  updateKeyPair,
  updateSigningKeyPair,
  saveDisplayPhotoKey,
  saveCoverPhotoKey,
  getDisplayPhotoKey,
  getCoverPhotoKey,
  getProfilePhotos,
  saveProfilePhotoEntry,
  removeProfilePhotoEntry,
  saveMyTokenAndId,
  insertPrivacyState,
  saveJitsiToken,
  getJitsiToken,

  // Users
  insertUser,
  getUsers,
  resolvePublicKeys,
  updateUser,

  // Groups
  getGrp,
  insertGrp,
  getGrps,
  updateGroupLastMessage,
  updateHiloMeta,
  updateGrpKey,
  updateGrpMembershipStatus,
  updateGrpMembers,
  resetUnread,
  resetUnreadHilo,
  markThreadUnread,

  // Messages
  getMsg,
  getMessagesByThreadId,
  getMessagesBySenderAndReceiverId, // @deprecated - use getMessagesByThreadId
  getMsgsByGroupId,
  getMsgsByCommunityId,
  insertMsg,
  updateMsg,
  deleteMsg,
  deleteMsgsByGroupId,
  deleteMsgsBySenderAndReceiverId,
  deleteMsgsByCommunityId,
  deleteMessagesForThread,
  getLatestMsgByGroupId,
  getLatestMsgByCommunityId,
  searchMsgs,
  getMessagesByGroupId,
  ackMessage,
  getOlderMessagesByGroupId,
  getOlderMessagesBySenderAndReceiverId,
  destroyAllHiData,
  messageExists,

  // Communities
  getCommunity,
  getCommunities,
  getActiveCommunities,
  insertCommunity,
  insertCommunities,
  updateCommunity,
  deactivateCommunity,
  deleteCommunity,
  deleteThread,
  replaceMembershipLists,
};
